import { G } from '@mobily/ts-belt';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { FileField, required, SelectInput, TextInput, useGetList, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import { GetBeneficiariesResponse } from '@Common/types/bo/beneficiaries';
import { ZodUkAccountNumber, ZodUkSortCode } from '@Common/types/zod/bank_account';

import { FileInput, ShowSection } from '@sh4res/bo-common/components';
import {
  RadioButtonGroupInput,
  RadioButtonGroupInputChoices,
} from '@sh4res/bo-common/components/RadioButtonGroupInput';
import { useWatchObject } from '@sh4res/bo-common/hooks';
import { isThirdPartyOnlyWithdrawal } from '@sh4res/bo-common/resources/b2b/withdrawals/common';
import { B2bWithdrawalFormValues } from '@sh4res/bo-common/resources/b2b/withdrawals/types';
import { allCountryCodesChoices } from '@sh4res/bo-common/utils/countryChoices';

const testId = 'BoExternalB2bWithdrawalsCreatePaymentStep';

const formatAccountDetails = (beneficiary: GetBeneficiariesResponse[0]): string => {
  const { account_details } = beneficiary;
  if (account_details.iban) {
    return account_details.iban;
  }
  if (account_details.sort_code && account_details.account_number) {
    return `${account_details.sort_code} / ${account_details.account_number}`;
  }
  return '-';
};

const validateScanSortCode = (value?: string) => {
  if (!ZodUkSortCode.safeParse(value).success) {
    return 'resources.bo_common.b2b.withdrawals.create.validation.scan_sort_code_invalid';
  }
  return undefined;
};

const validateScanAccountNumber = (value?: string) => {
  if (!ZodUkAccountNumber.safeParse(value).success) {
    return 'resources.bo_common.b2b.withdrawals.create.validation.scan_account_number_invalid';
  }
  return undefined;
};

export const PaymentStep = () => {
  const translate = useTranslate();
  const { setValue, unregister } = useFormContext();

  const {
    user_id: userId,
    b2b_company_id: companyId,
    beneficiary_id: selectedBeneficiaryId,
    is_deceased: isDeceased,
    early_reason: earlyReason,
  } = useWatchObject<B2bWithdrawalFormValues>()([
    'user_id',
    'b2b_company_id',
    'beneficiary_id',
    'is_deceased',
    'early_reason',
  ]);

  const thirdPartyOnly = isThirdPartyOnlyWithdrawal({ isDeceased, earlyReason });
  const isThirdPartySelected = selectedBeneficiaryId === 'third_party';

  const accountType: string = useWatch({
    name: 'third_party_bank_account.account_type',
    defaultValue: 'iban',
  });

  const entityType: string = useWatch({
    name: 'third_party_bank_account.entity_type',
    defaultValue: 'individual',
  });

  const { data: employeeBeneficiaries, isLoading: isLoadingEmployee } = useGetList<
    GetBeneficiariesResponse[0]
  >(
    'beneficiaries',
    {
      filter: {
        user_id: userId,
        is_active: true,
        approval_status: 'approved',
        reuse_beneficiary: true,
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'created_at', order: 'DESC' },
    },
    {
      enabled: G.isNotNullable(userId),
    },
  );

  const { data: companyBeneficiaries, isLoading: isLoadingCompany } = useGetList<
    GetBeneficiariesResponse[0]
  >(
    'beneficiaries',
    {
      filter: {
        b2b_company_id: companyId,
        is_active: true,
        approval_status: 'approved',
        reuse_beneficiary: true,
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'created_at', order: 'DESC' },
    },
    {
      enabled: G.isNotNullable(companyId),
    },
  );

  const isLoading = isLoadingEmployee || isLoadingCompany;

  const choices: RadioButtonGroupInputChoices = useMemo(() => {
    const result: RadioButtonGroupInputChoices = [];

    if (!thirdPartyOnly && employeeBeneficiaries && employeeBeneficiaries.length > 0) {
      employeeBeneficiaries.forEach((beneficiary) => {
        result.push({
          id: String(beneficiary.id),
          name: 'resources.bo_common.b2b.withdrawals.create.labels.employee_account_option',
          options: {
            iban: formatAccountDetails(beneficiary),
            name: beneficiary.name,
          },
        });
      });
    }

    if (!thirdPartyOnly && companyBeneficiaries && companyBeneficiaries.length > 0) {
      companyBeneficiaries.forEach((beneficiary) => {
        result.push({
          id: String(beneficiary.id),
          name: 'resources.bo_common.b2b.withdrawals.create.labels.company_account_option',
          options: {
            iban: formatAccountDetails(beneficiary),
            name: beneficiary.name,
          },
        });
      });
    }

    result.push({
      id: 'third_party',
      name: 'resources.bo_common.b2b.withdrawals.create.labels.third_party_account_option',
    });

    return result;
  }, [employeeBeneficiaries, companyBeneficiaries, thirdPartyOnly]);

  useEffect(() => {
    if (thirdPartyOnly && selectedBeneficiaryId !== 'third_party') {
      setValue('beneficiary_id', 'third_party');
      setValue('third_party_bank_account.account_type', 'iban');
    }
  }, [thirdPartyOnly, selectedBeneficiaryId, setValue]);

  const handleSelectionChange = (newValue: string) => {
    if (newValue === 'third_party') {
      setValue('third_party_bank_account.account_type', 'iban');
    } else {
      setValue('third_party_bank_account', undefined);
    }
  };

  if (isLoading) {
    return (
      <ShowSection
        leftAligned
        label="resources.bo_common.b2b.withdrawals.create.labels.select_bank_account"
        noCard={true}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </ShowSection>
    );
  }

  return (
    <ShowSection
      leftAligned
      label="resources.bo_common.b2b.withdrawals.create.labels.select_bank_account"
      noCard={true}>
      {thirdPartyOnly && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid={`${testId}_ThirdPartyOnlyAlert`}>
          {translate('resources.bo_common.b2b.withdrawals.create.labels.third_party_only_deceased')}
        </Alert>
      )}
      <RadioButtonGroupInput
        data-testid={`${testId}_BeneficiaryInput`}
        source="beneficiary_id"
        choices={choices}
        required={true}
        onChange={handleSelectionChange}
      />

      {isThirdPartySelected && (
        <Box sx={{ mt: 2, pl: 4, borderLeft: '2px solid', borderColor: 'primary.main' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {translate('resources.bo_common.b2b.withdrawals.create.labels.third_party_details')}
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            {translate('resources.bo_common.b2b.withdrawals.create.labels.account_type')}
          </Typography>
          <RadioGroup
            row
            value={accountType}
            onChange={(event) => {
              setValue('third_party_bank_account.account_type', event.target.value);
              unregister([
                'third_party_bank_account.iban',
                'third_party_bank_account.account_number',
                'third_party_bank_account.sort_code',
                'third_party_bank_account.bic',
              ]);
            }}
            sx={{ mb: 2 }}>
            <FormControlLabel
              value="iban"
              control={<Radio data-testid={`${testId}_AccountTypeIbanRadio`} />}
              label={translate(
                'resources.bo_common.b2b.withdrawals.create.labels.account_type_iban',
              )}
            />
            <FormControlLabel
              value="intl"
              control={<Radio data-testid={`${testId}_AccountTypeIntlRadio`} />}
              label={translate(
                'resources.bo_common.b2b.withdrawals.create.labels.account_type_intl',
              )}
            />
            <FormControlLabel
              value="scan"
              control={<Radio data-testid={`${testId}_AccountTypeScanRadio`} />}
              label={translate(
                'resources.bo_common.b2b.withdrawals.create.labels.account_type_scan',
              )}
            />
          </RadioGroup>

          <Typography variant="body2" sx={{ mb: 1 }}>
            {translate('resources.bo_common.b2b.withdrawals.create.labels.entity_type')}
          </Typography>
          <RadioGroup
            row
            value={entityType}
            onChange={(event) => {
              const newType = event.target.value;
              setValue('third_party_bank_account.entity_type', newType);
              if (newType === 'individual') {
                setValue('third_party_bank_account.company_name', undefined);
              } else {
                setValue('third_party_bank_account.first_name', undefined);
                setValue('third_party_bank_account.last_name', undefined);
              }
            }}
            sx={{ mb: 2 }}>
            <FormControlLabel
              value="individual"
              control={<Radio data-testid={`${testId}_EntityTypeIndividualRadio`} />}
              label={translate(
                'resources.bo_common.b2b.withdrawals.create.labels.entity_individual',
              )}
            />
            <FormControlLabel
              value="institution"
              control={<Radio data-testid={`${testId}_EntityTypeInstitutionRadio`} />}
              label={translate(
                'resources.bo_common.b2b.withdrawals.create.labels.entity_institution',
              )}
            />
          </RadioGroup>

          {entityType === 'individual' && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextInput
                data-testid={`${testId}_FirstNameInput`}
                source="third_party_bank_account.first_name"
                label="resources.bo_common.b2b.withdrawals.create.labels.first_name"
                validate={required()}
                fullWidth
              />
              <TextInput
                data-testid={`${testId}_LastNameInput`}
                source="third_party_bank_account.last_name"
                label="resources.bo_common.b2b.withdrawals.create.labels.last_name"
                fullWidth
              />
            </Box>
          )}

          {entityType === 'institution' && (
            <TextInput
              data-testid={`${testId}_CompanyNameInput`}
              source="third_party_bank_account.company_name"
              label="resources.bo_common.b2b.withdrawals.create.labels.company_name"
              validate={required()}
              fullWidth
            />
          )}

          {accountType === 'iban' && (
            <TextInput
              data-testid={`${testId}_IbanInput`}
              source="third_party_bank_account.iban"
              label="resources.bo_common.b2b.withdrawals.create.labels.iban"
              validate={required()}
              fullWidth
            />
          )}

          {accountType === 'scan' && (
            <TextInput
              data-testid={`${testId}_SortCodeInput`}
              source="third_party_bank_account.sort_code"
              label="resources.bo_common.b2b.withdrawals.create.labels.sort_code"
              validate={[required(), validateScanSortCode]}
              fullWidth
            />
          )}

          {(accountType === 'intl' || accountType === 'scan') && (
            <TextInput
              data-testid={`${testId}_AccountNumberInput`}
              source="third_party_bank_account.account_number"
              label="resources.bo_common.b2b.withdrawals.create.labels.account_number"
              validate={
                accountType === 'scan' ? [required(), validateScanAccountNumber] : required()
              }
              fullWidth
            />
          )}

          <TextInput
            data-testid={`${testId}_BicInput`}
            source="third_party_bank_account.bic"
            label="resources.bo_common.b2b.withdrawals.create.labels.bic"
            validate={required()}
            fullWidth
          />

          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
            {translate('resources.bo_common.b2b.withdrawals.create.labels.beneficiary_address')}
          </Typography>
          <TextInput
            data-testid={`${testId}_AddressStreetInput`}
            source="third_party_bank_account.address.street"
            label="resources.bo_common.b2b.withdrawals.create.labels.address_street"
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextInput
              data-testid={`${testId}_AddressPostcodeInput`}
              source="third_party_bank_account.address.postcode"
              label="resources.bo_common.b2b.withdrawals.create.labels.address_postcode"
              fullWidth
            />
            <TextInput
              data-testid={`${testId}_AddressCityInput`}
              source="third_party_bank_account.address.city"
              label="resources.bo_common.b2b.withdrawals.create.labels.address_city"
              fullWidth
            />
          </Box>
          <SelectInput
            data-testid={`${testId}_AddressCountrySelectInput`}
            source="third_party_bank_account.address.country"
            label="resources.bo_common.b2b.withdrawals.create.labels.address_country"
            choices={allCountryCodesChoices}
            fullWidth
          />

          <FileInput
            data-testid={`${testId}_RibInput`}
            source="third_party_bank_account.rib"
            label={translate('resources.bo_common.b2b.withdrawals.create.labels.rib_upload')}
            accept=".pdf"
            isRequired={true}
            validate={required()}>
            <FileField data-testid={`${testId}_RibField`} source="src" title="title" />
          </FileInput>
        </Box>
      )}
    </ShowSection>
  );
};
